import { FC, useEffect, useMemo, useState } from 'react';

import { Button, Stack, styled } from '@mui/material';
import { useChannel } from 'ably/react';

import { UnitApi } from '@/api';
import { withLoader } from '@/hocs';
import { useCFSContext, useUnitListFilter } from '@/hooks';
import { IUnit, Role } from '@/models';
import { useAgencyStore, useToastStore, useUserStore } from '@/store';

import { UnitItem } from './UnitItem';
import {
  default as UnitShiftAssignModal,
  UnitShiftAssignModalProps,
} from './UnitShiftAssignModal';
import { CommonUnitsProps } from './UnitShiftList';

const Root = styled(Stack)(() => ({
  flex: 1,
  padding: 15,
  overflow: 'auto',
}));

const OffDutyUnits: FC<CommonUnitsProps> = ({ filterValues, searchText }) => {
  const { updateToast } = useToastStore();
  const { cfs } = useCFSContext();
  const { account, user } = useUserStore();
  const { agencies } = useAgencyStore();

  const [resourceModal, setResourceModal] = useState<
    Omit<UnitShiftAssignModalProps, 'onClose' | 'showLoader' | 'hideLoader'>
  >({
    open: false,
  });
  const [offDutyUnits, setOffDutyUnits] = useState<IUnit[]>([]);

  const offDutyUnitsData = useUnitListFilter({
    filterValues,
    cfs,
    unitData: offDutyUnits,
    searchText,
  }) as IUnit[];

  useChannel(`account:${account?._id}:unit`, () => {
    fetchOffDutyUnits();
  });

  useEffect(() => {
    fetchOffDutyUnits();
  }, []);

  const fetchOffDutyUnits = async () => {
    try {
      const agencyId = new URLSearchParams(window.location.search).get(
        'agencyId',
      );
      const filter = JSON.stringify({
        status: 'OFF_DUTY',
        isStandByWhenOffDuty: false,
      });
      const res = await UnitApi.getUnits({
        filter,
        limit: 1000,
        ...(agencyId ? { agencyId } : {}),
        skipAgencyHeader: user?.roles.some((role) =>
          role.role.includes(Role.DISPATCHER),
        ),
      });
      const results = agencyId
        ? res.data.results.filter((unit) => unit.agency === agencyId)
        : res.data.results;
      setOffDutyUnits(results);
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  const onOpenModal = (unit: IUnit) => {
    setResourceModal({
      open: true,
      unit,
    });
  };

  const onCloseModal = () => {
    setResourceModal({
      open: false,
    });
  };

  const unitShiftSortFn = (a: IUnit, b: IUnit) => {
    if (!cfs?.assignedAgencies) return 0; // no sort if no assignedAgencies

    const aHasAgency = cfs.assignedAgencies.includes(a.agency);
    const bHasAgency = cfs.assignedAgencies.includes(b.agency);

    return Number(bHasAgency) - Number(aHasAgency);
  };

  const sortedOffDutyUnits = useMemo(() => {
    return offDutyUnitsData.sort(unitShiftSortFn);
  }, [offDutyUnitsData, cfs?.assignedAgencies]);

  return (
    <Root flex={1}>
      {sortedOffDutyUnits.map((unit, index) => (
        <UnitItem
          key={index}
          {...unit}
          agency={agencies.find((agency) => agency._id === unit.agency)}
        >
          <Button
            color="error"
            variant="contained"
            size="small"
            onClick={() => onOpenModal(unit)}
          >
            On-duty
          </Button>
        </UnitItem>
      ))}
      <UnitShiftAssignModal
        showScheduleOption
        {...resourceModal}
        onClose={onCloseModal}
      />
    </Root>
  );
};

export default withLoader(OffDutyUnits);
